import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgIf} from '@angular/common';
import {finalize, firstValueFrom, Subject, takeUntil} from 'rxjs';
import {NgbActiveModal} from '@ng-bootstrap/ng-bootstrap';
import {ToastService} from '../../../toast/toast.service';
import {FileService} from '../../../../services/file.service';
import {UserService} from '../../../../services/user-service.service';
import {AuthService} from '../../../../services/auth.service';

@Component({
  selector: 'app-update-profile-image-modal',
  imports: [
    FormsModule,
    NgIf
  ],
  templateUrl: './update-profile-image-modal.component.html',
  styleUrl: './update-profile-image-modal.component.scss'
})
export class UpdateProfileImageModalComponent implements OnInit, OnDestroy {
  onDestroy$ = new Subject<boolean>();

  @Input() image!: any

  // Image upload
  isDropboxHovering = false
  isUploadImagesLoading = false

  submitted = false;

  constructor(private modal: NgbActiveModal,
              private toast: ToastService,
              private userService: UserService,
              private fileService: FileService,
              private authService: AuthService) {
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  submit() {
    if (!this.image) return;

    const imageId = this.image._id

    this.submitted = true
    this.userService.updateImage(imageId).pipe(
      takeUntil(this.onDestroy$),
      finalize(() => this.submitted = false))
      .subscribe(() => {
        this.authService.refreshCurrentUser()

        this.toast.success("Your profile image updated.")
        this.modal.close()
      })
  }

  close() {
    this.modal.close()
  }

  fileDragEnter(event: any) {
    this.isDropboxHovering = true;
    event.preventDefault();
  }

  fileDragLeave() {
    this.isDropboxHovering = false;
  }

  fileDrop(event: any) {
    event.preventDefault();
    event.stopPropagation();
    this.uploadFiles(event.dataTransfer.files).finally();
  }

  uploadFilesClick(event: any) {
    this.uploadFiles(event.target.files).finally();
  }

  async uploadFiles(fileList: FileList) {
    this.isDropboxHovering = false

    if (this.isUploadImagesLoading) {
      return
    }

    const files = Array.from(fileList)
    const file = files[0]

    this.isUploadImagesLoading = true
    try {

      // Validate file size
      if (!this.fileService.validateSize(file, 2)) {
        this.toast.failure('Maximum file size is 2 MB');
        return;
      }

      // Read the file
      const imageFile = await this.fileService.fileReader(file) as ArrayBuffer
      const image = await this.fileService.imageReader(imageFile) as HTMLImageElement

      // Create a placeholder image file
      const contentType = file.type
      const imageWidth = image.width
      const imageHeight = image.height
      const createdImageFile: any = await firstValueFrom(this.fileService.createImageFile(contentType, imageWidth, imageHeight, null).pipe(takeUntil(this.onDestroy$)))

      // Upload image to S3
      await firstValueFrom(this.fileService.uploadFileToS3(createdImageFile.s3Path, file).pipe(takeUntil(this.onDestroy$)))

      // Create a user image file
      const createdUserImageFile: any = await firstValueFrom(this.fileService.createUserImage(createdImageFile._id).pipe(takeUntil(this.onDestroy$)))

      // Get user image file
      const userImageFile: any = await firstValueFrom(this.fileService.getById(createdUserImageFile._id).pipe(takeUntil(this.onDestroy$)))

      this.image = {_id: userImageFile._id, filePath: userImageFile.filePath};
    } finally {
      this.isUploadImagesLoading = false
    }
  }

}
